import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Table,
  message,
  Tooltip,
  Popconfirm,
  Tag,
  Select,
  Space,
  Typography,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CloudUploadOutlined,
  StopOutlined,
  SyncOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ROW_GUTTER } from "constants/ThemeConstant";
import { useMyContext } from "Context/MyContextProvider";
import {
  listMetaFlows,
  publishMetaFlow,
  deprecateMetaFlow,
  deleteMetaFlow,
  syncMetaFlows,
} from "services/metaFlowService";
const { Text } = Typography;
const { Option } = Select;

const STATUS_COLORS = {
  DRAFT: "default",
  PUBLISHED: "success",
  DEPRECATED: "warning",
  BLOCKED: "error",
  THROTTLED: "orange",
};

const FlowList = () => {
  const { Permisson, userRole } = useMyContext();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [flowsList, setFlowsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [actionFlowId, setActionFlowId] = useState(null);

  const canCreate = Permisson?.includes("Create flows");
  const canDelete = Permisson?.includes("Delete flows");

  const fetchFlows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listMetaFlows();
      setFlowsList(data?.data || []);
    } catch (error) {
      console.error("Error fetching MetaFlows:", error);
      message.error("Failed to fetch MetaFlows. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const data = await syncMetaFlows();
      setFlowsList(data?.data || []);
      message.success("MetaFlows synced from Meta");
    } catch (error) {
      message.error("Failed to sync MetaFlows");
    } finally {
      setSyncing(false);
    }
  };

  const handlePublish = async (flowId) => {
    try {
      setActionFlowId(flowId);
      await publishMetaFlow(flowId);
      message.success("MetaFlow published");
      await fetchFlows();
    } catch (error) {
      message.error(
        error?.response?.data?.error?.message || "Failed to publish MetaFlow"
      );
    } finally {
      setActionFlowId(null);
    }
  };

  const handleDeprecate = async (flowId) => {
    try {
      setActionFlowId(flowId);
      await deprecateMetaFlow(flowId);
      message.success("MetaFlow deprecated");
      await fetchFlows();
    } catch (error) {
      message.error("Failed to deprecate MetaFlow");
    } finally {
      setActionFlowId(null);
    }
  };

  const handleDelete = async (flowId) => {
    try {
      setActionFlowId(flowId);
      await deleteMetaFlow(flowId);
      message.success("MetaFlow deleted");
      setFlowsList((prev) => prev.filter((flow) => flow.id !== flowId));
    } catch (error) {
      message.error("Failed to delete MetaFlow");
    } finally {
      setActionFlowId(null);
    }
  };

  const filteredFlows = flowsList.filter((flow) => {
    const matchesSearch =
      flow.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      flow.id?.includes(searchText) ||
      flow.endpoint_uri?.toLowerCase().includes(searchText.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || flow.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"}>{status || "—"}</Tag>
      ),
    },
    {
      title: "Category",
      dataIndex: "categories",
      key: "categories",
      render: (categories) =>
        Array.isArray(categories) && categories.length > 0 ? (
          <Tag>{categories[0]}</Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Type",
      key: "type",
      render: (_, record) =>
        record.endpoint_uri ? (
          <Tag color="blue">Dynamic</Tag>
        ) : (
          <Tag>Static</Tag>
        ),
    },
    ...(userRole === "Admin"
      ? [
        {
          title: "Endpoint",
          dataIndex: "endpoint_uri",
          key: "endpoint_uri",
          ellipsis: true,
          render: (uri) =>
            uri ? (
              <Tooltip title={uri}>
                <a href={uri} target="_blank" rel="noopener noreferrer">
                  <LinkOutlined /> {uri}
                </a>
              </Tooltip>
            ) : (
              "—"
            ),
        },
      ]
      : []),
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 200,
      render: (_, record) => (
        <Space size="small" wrap>
          {canCreate &&
            (record.status === "DRAFT" || record.status === "PUBLISHED") && (
              <Tooltip
                title={
                  record.status === "PUBLISHED"
                    ? "Edit (saving moves flow back to Draft)"
                    : "Edit MetaFlow"
                }
              >
                <Link to={`edit/${record.id}`}>
                  <Button type="link" icon={<EditOutlined />} />
                </Link>
              </Tooltip>
            )}
          {canCreate && record.status === "DRAFT" && (
            <Tooltip title="Publish">
              <Popconfirm
                title="Publish this MetaFlow?"
                onConfirm={() => handlePublish(record.id)}
              >
                <Button
                  type="link"
                  icon={<CloudUploadOutlined />}
                  loading={actionFlowId === record.id}
                />
              </Popconfirm>
            </Tooltip>
          )}
          {canCreate && record.status === "PUBLISHED" && (
            <Tooltip title="Deprecate">
              <Popconfirm
                title="Deprecate this MetaFlow? It can no longer be sent."
                onConfirm={() => handleDeprecate(record.id)}
              >
                <Button
                  type="link"
                  icon={<StopOutlined />}
                  loading={actionFlowId === record.id}
                />
              </Popconfirm>
            </Tooltip>
          )}
          {(canDelete || canCreate) && record.status === "DRAFT" && (
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this MetaFlow?"
                description="This cannot be undone."
                onConfirm={() => handleDelete(record.id)}
              >
                <Button
                  danger
                  type="link"
                  icon={<DeleteOutlined />}
                  loading={actionFlowId === record.id}
                />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Row align="top" gutter={ROW_GUTTER}>
      <Col xs={24}>
        <motion.div
          layout
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <Card
            title={
              <Row justify="space-between" align="middle" gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <h2 style={{ margin: 0 }}>MetaFlows</h2>
                  <Text type="secondary">
                    WhatsApp Flows managed via Meta Graph API
                  </Text>
                </Col>
                <Col xs={24} md={16}>
                  <Row gutter={[12, 12]} justify="end" align="middle">
                    <Col xs={24} sm={8} md={6}>
                      <Select
                        value={statusFilter}
                        onChange={setStatusFilter}
                        style={{ width: "100%" }}

                      >
                        <Option value="all">All statuses</Option>
                        <Option value="DRAFT">Draft</Option>
                        <Option value="PUBLISHED">Published</Option>
                        <Option value="DEPRECATED">Deprecated</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                      <Input
                        allowClear
                        placeholder="Search flows..."
                        value={searchText}
                        suffix={<SearchOutlined />}

                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </Col>
                    {canCreate && (
                      <>
                        <Col xs={12} sm={4} md={3}>
                          <Button
                            block

                            icon={<SyncOutlined />}
                            loading={syncing}
                            onClick={handleSync}
                          >
                            Sync
                          </Button>
                        </Col>
                        <Col xs={12} sm={4} md={4}>
                          <Link to="create-flow">
                            <Button type="primary" block>
                              <PlusOutlined /> New MetaFlow
                            </Button>
                          </Link>
                        </Col>
                      </>
                    )}
                  </Row>
                </Col>
              </Row>
            }
          >
            <Table
              loading={loading}
              columns={tableColumns}
              dataSource={filteredFlows}
              rowKey="id"
              scroll={{ x: "max-content" }}
              locale={{
                emptyText: loading ? "Loading MetaFlows..." : "No MetaFlows found",
              }}
            />
          </Card>
        </motion.div>
      </Col>
    </Row>
  );
};

export default FlowList;
